import { ArrowUpTrayIcon, DocumentPlusIcon } from "@heroicons/react/24/outline";
import { useStore } from "@nanostores/react";
import { openPage, redirectPage } from "@nanostores/router";
import { ReactElement, useState } from "react";

import { GraphSummary } from "../api";
import { $modifiableGraphMeta } from "../stores/graph/metadata.ts";
import { $recentlyOpenedGraphs } from "../stores/graph/persistence.ts";
import { $router } from "../stores/router";
import { Button } from "./catalyst/Button";
import { CSVUploadModal } from "./CSVUploadModal.tsx";
import { GraphUpsertDialog } from "./GraphUpsertDialog.tsx";
import { Tabs } from "./Tabs";

const removeFromRecentGraphs = (
  currentGraphs: GraphSummary[],
  graphId: string,
): GraphSummary[] => {
  return currentGraphs.filter((graph) => graph.id !== graphId);
};

export const GraphToolbar = (): ReactElement => {
  const modifiableGraph = useStore($modifiableGraphMeta);
  const recentlyOpenedGraphs = useStore($recentlyOpenedGraphs);
  const router = useStore($router);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const handleCloseGraph = (id: string): void => {
    const newRecentlyOpenedGraphs = removeFromRecentGraphs(
      recentlyOpenedGraphs,
      id,
    );

    $recentlyOpenedGraphs.set(newRecentlyOpenedGraphs);

    if (newRecentlyOpenedGraphs.length === 0) {
      openPage($router, "index");
      return;
    }

    if (router?.route === "graph" && router.params.id === id) {
      redirectPage($router, "graph", {
        id: newRecentlyOpenedGraphs[0].id,
      });
    }
  };

  const handleAddClick = (): void => {
    $modifiableGraphMeta.set({ type: "create" });
  };

  const handleUploadClick = (): void => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = (): void => {
    setIsUploadModalOpen(false);
  };

  return (
    <div className="ring-.75 flex rounded-lg bg-zinc-900/90 p-2 shadow-lg ring-zinc-600">
      {(modifiableGraph?.type === "create" ||
        modifiableGraph?.type === "copy") && <GraphUpsertDialog />}
      <CSVUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleUploadModalClose}
      />

      <div className="flex items-center gap-2">
        {recentlyOpenedGraphs.length !== 0 && (
          <div className="flex gap-4 border-r pl-4 pr-4 dark:border-white/10">
            <Tabs
              currentId={
                router?.route === "graph" ? router.params.id : undefined
              }
              items={recentlyOpenedGraphs.map((graph) => ({
                id: graph.id,
                name: graph.name,
                onClick: (): void => {
                  openPage($router, "graph", { id: graph.id });
                },
                onClose: (event): void => {
                  event.stopPropagation();
                  handleCloseGraph(graph.id);
                },
              }))}
            />
          </div>
        )}

        <Button onClick={handleAddClick} plain size="xs">
          <DocumentPlusIcon className="h-4 w-4" />
        </Button>

        <Button onClick={handleUploadClick} plain size="xs">
          <ArrowUpTrayIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
